import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import {
  Review,
  getQueryKey as getQueryKeyIntegrationReview,
} from '../useQueryIntegrationReview';

import type { PublishingForm } from './types';

const MUTATION_KEY = 'mutate-submit-publishing-form';

function formatToRequestBody(publishingForm: PublishingForm, version: string) {
  const {
    id,
    // Integration readiness & API ownership.
    hasApiOwnership,
    apiEndpointsUsed,
    ownsAllEndpoints,
    hasPermissionForEndpoints,
    usesProductionEndpoints,
    paidApi,
    // Test account for us.
    testAccountUsername,
    testAccountPassword,
    testAccountOnlySupportsSso,
    ssoTestAccountCreated,
    testAccountNotes,
    // App details.
    hasBeenLaunched,
    isReplacementIntegration,
    alreadyManagingSimilarIntegration,
    linkToOtherIntegrations,
    newIntegrationRationale,
    homepageUrl,
    apiDocsUrl,
    apiDocumentationNotes,
    primaryColor,
    twitterUsername,
    // Contact details.
    marketingContactEmail,
    securityContactEmail,
    technicalContactEmail,
    expectedResponseTime,
    // Compliance & platform rules.
    companyBaseCountry,
    transmitsSensitiveValues,
    collectsFinancialInfo,
    usesThirdPartyApis,
    permissionForThirdPartyApis,
    usesZapierBranding,
    usesCurrentZapierBranding,
  } = publishingForm;

  return {
    id: String(id),
    version,
    // Integration readiness & API ownership.
    has_api_ownership: hasApiOwnership,
    api_endpoints_used: apiEndpointsUsed,
    owns_all_endpoints: ownsAllEndpoints,
    has_permission_for_endpoints: hasPermissionForEndpoints,
    uses_production_endpoints: usesProductionEndpoints,
    paid_api: paidApi,
    // Test account for us.
    test_account_username: testAccountUsername,
    test_account_password: testAccountPassword,
    test_account_only_supports_sso: testAccountOnlySupportsSso,
    sso_test_account_created: ssoTestAccountCreated,
    test_account_notes: testAccountNotes,
    // App details.
    has_been_launched: hasBeenLaunched,
    is_replacement_integration: isReplacementIntegration,
    already_managing_similar_integration: alreadyManagingSimilarIntegration,
    link_to_other_integrations: linkToOtherIntegrations,
    new_integration_rationale: newIntegrationRationale,
    homepage_url: homepageUrl,
    api_docs_url: apiDocsUrl,
    api_documentation_notes: apiDocumentationNotes,
    primary_color: primaryColor,
    twitter_username: twitterUsername,
    // Contact details.
    marketing_contact_email: marketingContactEmail,
    security_contact_email: securityContactEmail,
    technical_contact_email: technicalContactEmail,
    expected_response_time: expectedResponseTime,
    // Compliance & platform rules.
    company_base_country: companyBaseCountry,
    transmits_sensitive_values: transmitsSensitiveValues,
    collects_financial_info: collectsFinancialInfo,
    uses_third_party_apis: usesThirdPartyApis,
    permission_for_third_party_apis: permissionForThirdPartyApis,
    uses_zapier_branding: usesZapierBranding,
    uses_current_zapier_branding: usesCurrentZapierBranding,
  };
}

function useMutationSubmitPublishingForm(
  args: { integrationId: number },
  options?: UseMutationOptions<
    void,
    Error,
    { publishingForm: PublishingForm; version: string },
    { publishingForm: PublishingForm; review: Review }
  >
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKeyIntegrationReview = getQueryKeyIntegrationReview({
    integrationId,
  });

  const mutationKey = MUTATION_KEY;

  const mutation = useMutation({
    mutationKey,
    async mutationFn({
      publishingForm,
      version,
    }: {
      publishingForm: PublishingForm;
      version: string;
    }) {
      const url = new URL(
        `api/platform/cli/apps/${encodeURIComponent(
          integrationId
        )}/versions/${encodeURIComponent(version)}/activate`,
        ZAPIER_APP_BASE_URL
      );

      return fetchJson(url.href, {
        body: JSON.stringify(formatToRequestBody(publishingForm, version)),
        method: 'POST',
      });
    },
    onError: (error: any, _variables) =>
      notify.failure(
        `There was a problem submitting your review request: ${
          JSON.parse(error?.responseText ?? {}).errors?.[0] ?? 'unknown'
        }. Please try again later or contact support.`,
        mutationKey
      ),
    onSuccess: () => {
      notify.success(
        'You’ve submitted your review request for publication on the Zapier Platform!'
      );
      queryClient.invalidateQueries(queryKeyIntegrationReview);
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationSubmitPublishingForm;
